// SettingsPanel — 設定画面 (設計書 §5.3)。settings.ts (mj.settings.v1) を直接読み書きする
// カード列 UI。行高 48px・タップ領域 ≥44px・日本語ラベル。
// 音量スライダは sfx.setVolume を即時反映（永続化は settings 側、sound.ts は持たない: D10）。
// qol.*（鳴き確認スキップ等）はここに出さない — 対局設定ドロワー側 (integrate, §10.3) の担当。
import type { CSSProperties, ReactNode } from 'react';
import { useSettings, patchSettings } from './settings';
import type { Settings, MotionIntensity, TileScale, DiscardConfirm } from './settings';
import { sfx } from './sound';

// ---------------------------------------------------------------------------
// 小物（このファイル内だけで使う）
// ---------------------------------------------------------------------------

const CARD: CSSProperties = {
  background: 'var(--surface-card)',
  border: '1px solid var(--border-panel)',
  borderRadius: 'var(--r-md)',
  padding: 'var(--sp-2) var(--sp-4)',
  boxShadow: 'var(--shadow-1)',
};
const CARD_TITLE: CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.12em',
  color: 'var(--gold-300)',
  padding: 'var(--sp-2) 0',
  userSelect: 'none',
};
const ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-3)',
  minHeight: 48, // §5.3 行高
  borderTop: '1px solid rgba(255,255,255,0.06)',
};
const LABEL: CSSProperties = { flex: '1 1 auto', fontSize: 14, color: 'var(--text-hi)' };
const SUB: CSSProperties = { fontSize: 11, color: 'var(--text-low)', lineHeight: 1.45 };

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={CARD}>
      <h2 style={{ ...CARD_TITLE, margin: 0 }}>{title}</h2>
      {children}
    </section>
  );
}

function Row({ label, sub, children }: { label: string; sub?: string | undefined; children: ReactNode }) {
  return (
    <div style={ROW}>
      <div style={LABEL}>
        <div>{label}</div>
        {sub && <div style={SUB}>{sub}</div>}
      </div>
      {children}
    </div>
  );
}

/** トグルスイッチ（見た目 44×24、ボタン全体で ≥44px のタップ領域）。 */
function Toggle({ checked, onChange, label }: { checked: boolean; onChange(next: boolean): void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      style={{
        flex: '0 0 auto',
        minWidth: 44,
        minHeight: 44,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 44,
          height: 24,
          borderRadius: 'var(--r-full)',
          background: checked ? 'var(--grad-gold)' : 'var(--surface-well)',
          border: `1px solid ${checked ? 'var(--gold-700)' : 'var(--border-panel)'}`,
          position: 'relative',
          transition: 'background 150ms ease-out',
          display: 'inline-block',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: checked ? 22 : 2,
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: checked ? 'var(--gold-100)' : 'var(--text-mid)',
            boxShadow: 'var(--shadow-1)',
            transition: 'left 150ms ease-out',
          }}
        />
      </span>
    </button>
  );
}

/** セグメント選択（各ボタン ≥44px タップ領域）。 */
function Segmented<T extends string | number>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange(next: T): void;
  label: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      style={{
        flex: '0 0 auto',
        display: 'flex',
        background: 'var(--surface-well)',
        borderRadius: 'var(--r-sm)',
        border: '1px solid var(--border-panel)',
        overflow: 'hidden',
      }}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            style={{
              minHeight: 44,
              padding: '0 12px',
              fontSize: 12,
              fontWeight: active ? 700 : 400,
              border: 'none',
              cursor: 'pointer',
              background: active ? 'var(--grad-gold)' : 'transparent',
              color: active ? 'var(--gold-ink)' : 'var(--text-mid)',
              fontFamily: 'inherit',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** 設定 → 実音量の反映（muted 優先）。聴感補正(v*v)は sound.ts 側の責務。 */
function applyVolume(s: Pick<Settings['audio'], 'masterVolume' | 'muted'>): void {
  sfx.setVolume(s.muted ? 0 : s.masterVolume / 100);
}

// ---------------------------------------------------------------------------
// 本体
// ---------------------------------------------------------------------------

export function SettingsPanel() {
  const s = useSettings();

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 560,
        margin: '0 auto',
        padding: 'var(--sp-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
        boxSizing: 'border-box',
        color: 'var(--text-hi)',
        fontFamily: 'var(--font-ui)',
      }}
    >
      <h1 style={{ fontSize: 18, margin: '4px 0 0', color: 'var(--text-hi)' }}>設定</h1>

      <Card title="サウンド">
        <Row label="音量" sub={s.audio.muted ? 'ミュート中' : undefined}>
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={s.audio.masterVolume}
            aria-label="音量"
            onChange={(e) => {
              const v = Number(e.currentTarget.value);
              const next = patchSettings({ audio: { masterVolume: v } });
              applyVolume(next.audio); // 即時反映 (§5.3)
            }}
            style={{ flex: '1 1 auto', minHeight: 44, accentColor: 'var(--gold-400)', maxWidth: 200 }}
          />
          <span
            className="num"
            style={{ width: 34, textAlign: 'right', fontSize: 13, color: 'var(--text-mid)' }}
          >
            {s.audio.masterVolume}
          </span>
        </Row>
        <Row label="ミュート">
          <Toggle
            checked={s.audio.muted}
            label="ミュート"
            onChange={(b) => {
              const next = patchSettings({ audio: { muted: b } });
              applyVolume(next.audio);
            }}
          />
        </Row>
      </Card>

      <Card title="演出">
        <Row label="演出の強さ" sub="「自動」は OS の視差効果設定に従います">
          <Segmented<MotionIntensity>
            label="演出の強さ"
            value={s.motion.intensity}
            options={[
              { value: 'auto', label: '自動' },
              { value: 'full', label: 'フル' },
              { value: 'reduced', label: '控えめ' },
            ]}
            onChange={(v) => patchSettings({ motion: { intensity: v } })}
          />
        </Row>
      </Card>

      <Card title="表示">
        <Row label="回転卓" sub="1〜4人の対局で席を卓上配置します">
          <Toggle
            checked={s.display.orientedTable}
            label="回転卓"
            onChange={(b) => patchSettings({ display: { orientedTable: b } })}
          />
        </Row>
        <Row label="牌の大きさ">
          <Segmented<TileScale>
            label="牌の大きさ"
            value={s.display.tileScale}
            options={[
              { value: 0.85, label: '小' },
              { value: 1, label: '標準' },
              { value: 1.15, label: '大' },
            ]}
            onChange={(v) => patchSettings({ display: { tileScale: v } })}
          />
        </Row>
        <Row label="アシスト表示" sub="聴牌の金枠・有効打牌の補助など">
          <Toggle
            checked={s.display.assist}
            label="アシスト表示"
            onChange={(b) => patchSettings({ display: { assist: b } })}
          />
        </Row>
        <Row label="ツモ切りの色分け" sub="河のツモ切り牌をうっすら青く表示">
          <Toggle
            checked={s.display.tsumogiriTint}
            label="ツモ切りの色分け"
            onChange={(b) => patchSettings({ display: { tsumogiriTint: b } })}
          />
        </Row>
        <Row label="タイトル画面をスキップ" sub="起動時にそのまま対局を開始します">
          <Toggle
            checked={s.display.skipTitle}
            label="タイトル画面をスキップ"
            onChange={(b) => patchSettings({ display: { skipTitle: b } })}
          />
        </Row>
      </Card>

      <Card title="入力">
        <Row label="打牌の確認" sub="2タップ: 1度目で選択、2度目で打牌（誤タップ防止）">
          <Segmented<DiscardConfirm>
            label="打牌の確認"
            value={s.input.discardConfirm}
            options={[
              { value: 'single', label: '1タップ' },
              { value: 'double', label: '2タップ' },
            ]}
            onChange={(v) => patchSettings({ input: { discardConfirm: v } })}
          />
        </Row>
      </Card>

      <Card title="検証">
        <Row label="検証ツールを表示" sub="対局設定内の seed・ドラ指定などの開発ツール欄">
          <Toggle
            checked={s.dev.showTools}
            label="検証ツールを表示"
            onChange={(b) => patchSettings({ dev: { showTools: b } })}
          />
        </Row>
      </Card>
    </div>
  );
}
